import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';

import type { Settings } from '../api/client';
import { setDiagnosticsEnabled } from '../observability/sentry';

/**
 * Diagnostics consent — the client half of `settings.diagnostics_opt_in`.
 *
 * The setting is ACCOUNT level: the server owns it and it follows the user to
 * every device and surface (iOS, Android, web all read the same `/settings`).
 * This store exists to carry it to Faro, which cannot import an API client.
 *
 * The local copy is a CACHE, never the source of truth. Its only job is the
 * pre-auth window: `/settings` needs a token, so between app launch and a
 * successful sign-in there is no way to ask the server what the account wants —
 * and app launch, the OIDC redirect, the login screen and E2EE setup all sit
 * inside that window. Replaying the last known answer there is strictly better
 * than reporting unconditionally. Whenever the server does answer, it wins.
 *
 * The key is deliberately NOT scoped per user (contrast teamStore's
 * ACTIVE_TEAM_KEY): it has to be readable with no session at all. On a shared
 * device that means the previous account's preference governs the login screen
 * until someone signs in. That errs toward sending less, which is the right
 * direction to fail.
 */
const CACHE_KEY = '@cynosure/diagnostics_opt_in';

interface DiagnosticsState {
  /** Last value applied to the telemetry client. */
  enabled: boolean;
  /** True once the server's answer (not the cache) has been applied. */
  isAuthoritative: boolean;
  hydrateFromCache: () => Promise<void>;
  loadFromSettings: (settings: Settings) => void;
  /** Optimistic local apply for the settings toggle, before the PUT lands. */
  setEnabled: (enabled: boolean) => void;
}

/** Default true — matches the server default and preserves today's behaviour.
 *  See the note on `_diagnosticsEnabled` in sentry.native.ts. */
const DEFAULT_ENABLED = true;

export const useDiagnosticsStore = create<DiagnosticsState>((set, get) => ({
  enabled: DEFAULT_ENABLED,
  isAuthoritative: false,

  hydrateFromCache: async () => {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      // A server answer that arrived while this read was in flight outranks the
      // cache — do not let a slow disk overwrite the authoritative value.
      if (cached === null || get().isAuthoritative) return;
      const enabled = cached === 'true';
      set({ enabled });
      setDiagnosticsEnabled(enabled);
    } catch {
      // Unreadable cache is not worth failing a launch over; the default stands
      // until /settings answers.
    }
  },

  loadFromSettings: (settings: Settings) => {
    const enabled = settings.diagnostics_opt_in ?? DEFAULT_ENABLED;
    set({ enabled, isAuthoritative: true });
    setDiagnosticsEnabled(enabled);
    AsyncStorage.setItem(CACHE_KEY, String(enabled)).catch(() => {});
  },

  setEnabled: (enabled: boolean) => {
    set({ enabled });
    setDiagnosticsEnabled(enabled);
    AsyncStorage.setItem(CACHE_KEY, String(enabled)).catch(() => {});
  },
}));
